/* eslint-disable @typescript-eslint/no-loss-of-precision */
import * as React from 'react';
import * as navigate from 'app/area/shared/navigation';
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import { Numberbox, Textarea, Validation } from 'ui';
import { routeConstants } from 'app/routes/route-constants';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { createValidationError } from 'app/infrastructure/data/validation';
import { CodeActionType } from 'app/area/indicator-factors/item/types';
import { CodetableItemProps } from './types';
import { createFormatters } from 'app/infrastructure/data/date';
import styles from './indicator-factors-list-row.scss';

export const IndicatorFactorsRow: React.FC<CodetableItemProps> = ({
  breadcrumbs, codeItem, metadata, modifiedFactor, modifiedComment, readOnly, validation,
  fetchingDelete, changeFactor, changeComments, meatball, translation, locale
}) => {
  const [autoExpandable, handleAutoExpandable] = React.useState(false);

  const expandDown = () => {
    handleAutoExpandable(false);
    return false;
  };

  const expandUp = () => {
    handleAutoExpandable(true);
    return true;
  };

  const onClick = () => {
    navigate.to(routeConstants.indicatorFactorsAdministrationCodeFor(breadcrumbs, codeItem.id, CodeActionType.Edit));
  };

  const indicatorSubstanceUom = !codeItem?.indicatorSubstanceUom?.value ? '-' : codeItem?.indicatorSubstanceUom?.description;
  const substanceUom = !codeItem?.substanceUom?.value ? '-' : codeItem?.substanceUom?.description;
  const substanceCategory = codeItem?.substanceCategory?.value && '(' + codeItem?.substanceCategory?.description + ')';
  const fetching = fetchingDelete === codeItem?.id;
  const factor = modifiedFactor
    ? modifiedFactor.factor
    : isDefinedIncludingZero(codeItem?.factor) ? codeItem.factor : undefined;

  const factorValidation = modifiedFactor && !isDefinedIncludingZero(modifiedFactor.factor)
    ? createValidationError(translation.must_be_filled_out)
    : validation?.factor;

  const comment = modifiedComment
    ? modifiedComment.comments
    : codeItem?.comments;

  const hasErrors = factorValidation?.messages?.length ?? 0 > 0;

  const rowStyle = !!fetching
    ? styles.disabled
    : hasErrors
      ? `has-danger ${styles.error}`
      : modifiedFactor ? styles.edited : undefined;

  const formatters = createFormatters(locale);

  const dateFormatter = (date: Date) => formatters.formatDate(date, {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  const shouldExpand = (value: string) => value?.length > 25 || value?.includes('\n');

  const handleChangeComments = (value: string) => {
    shouldExpand(value) && handleAutoExpandable(true);
    changeComments(value);
  };

  return <tr className={rowStyle}>
    <td onClick={onClick}>{codeItem?.id}</td>
    <td onClick={onClick}>{`${codeItem?.substance?.description ?? ''} ${substanceCategory}`}</td>
    <td onClick={onClick}>{codeItem?.indicatorSubstance?.description}</td>
    {metadata.location && <td onClick={onClick}>
      {!!codeItem?.location?.value && codeItem?.location?.description}
    </td>}
    {metadata.unit && <td onClick={onClick}>
      {!!codeItem?.unit?.value && codeItem?.unit?.description}
    </td>}
    {metadata.source && <td onClick={onClick}>
      {!!codeItem?.source?.value && codeItem?.source?.description}
    </td>}
    {metadata.equipment && <td onClick={onClick}>
      {!!codeItem?.equipment?.value && codeItem?.equipment?.description}
    </td>}
    <td onClick={onClick}>{codeItem?.indicatorType?.description}</td>
    <td onClick={onClick}>{dateFormatter(codeItem?.validFrom)}</td>
    {readOnly
      ? <td onClick={onClick}>{isDefinedIncludingZero(codeItem?.factor)
        ? new Intl.NumberFormat(locale, { useGrouping: true, maximumFractionDigits: 15 }).format(codeItem?.factor)
        : ''
      }</td>
      : <td className={styles.form}>
        <Numberbox
          readOnly={false}
          value={factor}
          onChange={changeFactor}
          useThousand=" "
          minValue={-999999999999999.999999999999999}
          maxValue={999999999999999.999999999999999}
          maxDecimals={15}
          locale={locale}
          textInvalidRangeError={translation.must_be_within_range}
        />
        {!!factorValidation && <Validation level={factorValidation.level} messages={factorValidation.messages} />}
      </td>
    }
    <td onClick={onClick}>{`${indicatorSubstanceUom}/${substanceUom}`}</td>
    <td className={styles.form}>
      <Textarea
        className={styles.comments}
        autoExpandable={autoExpandable}
        maxLength={4000}
        readOnly={readOnly}
        value={comment}
        onChange={handleChangeComments} />
      {shouldExpand(comment) &&
        <div className={styles.arrows}>
          {autoExpandable && <MdKeyboardArrowUp onClick={expandDown} />}
          {!autoExpandable && <MdKeyboardArrowDown onClick={expandUp} />}
        </div>}
    </td>
    <td className={styles.meatball}>
      {meatball}
    </td>
  </tr>;
};
